import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ResaleFinalizedView from '../components/ResaleFinalizedView';
import ResaleInspectionWorkbench from '../components/ResaleInspectionWorkbench';
import { emptyResaleSections } from '../lib/resaleScore';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

let walkInDraftPromise = null;

async function createWalkInDraft(branchId) {
  let resolvedBranchId = branchId;
  if (!resolvedBranchId) {
    const { data: branches } = await supabase.from('branches').select('id').limit(1);
    resolvedBranchId = branches?.[0]?.id;
  }
  const { data, error: insertError } = await supabase
    .from('resale_inspection_reports')
    .insert({
      branch_id: resolvedBranchId,
      status: 'draft',
      sections: emptyResaleSections(),
    })
    .select('id, status, report_number')
    .single();
  if (insertError) throw new Error(insertError.message);
  return data;
}

export default function ResaleInspectionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const isNew = id === 'new';
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(!isNew);

  useEffect(() => {
    if (isNew) return;
    let alive = true;
    async function load() {
      setLoading(true);
      const { data, error: queryError } = await supabase
        .from('resale_inspection_reports')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (!alive) return;
      if (queryError) {
        setError(queryError.message);
        setLoading(false);
        return;
      }
      if (!data) {
        setError('Inspection report not found.');
        setLoading(false);
        return;
      }
      setReport(data);
      if (data.booking_id) {
        const { data: bookingRow } = await supabase
          .from('bookings')
          .select(
            'id, status, journey_stage, service_type, scheduled_at, created_at, checked_in_at, problem_description, branch_id, profiles:customer_id(first_name, last_name, full_name, phone, email), vehicles:vehicle_id(brand, model, year, plate_number, vin, mileage, engine, transmission), branches:branch_id(name, address, phone)'
          )
          .eq('id', data.booking_id)
          .maybeSingle();
        if (alive) setBooking(bookingRow);
      }
      setLoading(false);
    }
    load();
    return () => {
      alive = false;
    };
  }, [id, isNew]);

  useEffect(() => {
    if (!isNew) return;
    let alive = true;
    if (!walkInDraftPromise) {
      walkInDraftPromise = createWalkInDraft(profile?.branch_id).finally(() => {
        walkInDraftPromise = null;
      });
    }
    walkInDraftPromise
      .then((draft) => {
        if (!alive) return;
        navigate(`/resale-inspections/${draft.id}`, { replace: true });
      })
      .catch((err) => {
        if (!alive) return;
        setError(err.message || 'Could not create draft.');
      });
    return () => {
      alive = false;
    };
  }, [isNew, navigate, profile?.branch_id]);

  if (isNew) {
    if (error) return <p className="error">{error}</p>;
    return <p className="muted">Creating walk-in inspection…</p>;
  }

  if (loading) return <p className="muted">Loading inspection…</p>;
  if (error) return <p className="error">{error}</p>;
  if (!report) return null;

  return (
    <section className="resale-page">
      <header className="page-head resale-detail-head">
        <div>
          <Link to="/resale-inspections" className="ghost">
            Back to resale desk
          </Link>
          <h1>{report.report_number}</h1>
          <p className="lede">
            {report.status === 'finalized'
              ? 'Certified report ready to share with buyers.'
              : 'Complete all five steps, then publish the buyer link.'}
          </p>
        </div>
      </header>

      {report.status === 'finalized' ? (
        <ResaleFinalizedView
          report={report}
          booking={booking}
          onRefresh={setReport}
        />
      ) : (
        <ResaleInspectionWorkbench
          booking={booking}
          reportId={report.id}
          branchId={profile?.branch_id}
          onBookingChange={setBooking}
          onDeleted={() => navigate('/resale-inspections')}
          onFinalized={setReport}
        />
      )}
    </section>
  );
}
